import _ from 'lodash'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { IconButton, Stack, Theme, Tooltip, useMediaQuery } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import SettingsIcon from '@mui/icons-material/Settings'

import { ConsultationStatus, ConsultationType } from '@/core/models/consultation.model'
import { AvailabilityMeetingPlace } from '@/core/models/meeting-link.model'
import { OrderStatus } from '@/core/models/order.model'

// Update the MeetingLinkOrder type to be more specific
type MeetingLinkOrder = {
  _id: string
  consultation?: { _id: string }
  meetingPlace: {
    placeId?: string
    description: string
  }
  createdBy: {
    _id: string
    userInfo: {
      name: string
      surname: string
    }
  }
  speciality: string
  startDate: string
  status: OrderStatus
  files?: Array<any>
  meetingLink?: {
    isPrioritized?: boolean
  }
  comment?: string
  referenceId: string
  formSubmission?: any
  payment?: any
  requiresPayment?: boolean
  paymentCompleted?: boolean
  createdAt?: string
}

// Common order data type
type OrderData = {
  id: string
  consultationId?: string
  meetingPlace?: string
  meetingPlaceDescription: string
  patient: {
    id: string
    fullName: string
  } | null
  speciality: string
  startDate: string
  status: string
  filesCount?: number
  isPrioritized?: boolean
  comment?: string
  referenceId: string
  hasFormSubmission?: boolean
  payment?: any
  requiresPayment?: boolean
  paymentCompleted?: boolean
  createdAt?: string
}

// Props for both card and table views
interface CommonViewProps {
  data: OrderData[]
  loading?: boolean
  onAcceptOrder?: (orderId: string) => void
  onCancelOrder?: (orderId: string) => void
  onRefuseOrder?: (orderId: string) => void
  onStartLocalConsultation?: (orderId: string, patientId: string) => void
  onRefresh?: () => void
  onConvertOrder?: (orderId: string) => void
}

type OrdersCardViewProps = CommonViewProps
type OrdersTableProps = CommonViewProps & {
  isDoctor?: boolean
  manualPagination?: boolean
  totalCount: number
}

import { useCreateConsultationMutation } from '@/core/services/consultation'
import { useUpdateMeetingLinkOrderMutation, useLazySearchOrdersQuery } from '@/core/services/order'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { getApiErrorMessage } from '@/utils/errors'
import { isStaff } from '@/utils/type-guards'

import OrdersTable from '../components/OrdersTable'
import OrdersCardView from '../components/OrdersCardView'
import { ConvertOrderDialogContainer } from './ConvertOrderDialog'

const emptyOrders: MeetingLinkOrder[] = []

type OrdersTableContainerProps = {
  orders?: MeetingLinkOrder[]
  totalCount?: number
  onRefresh?: () => void
}

export default function OrdersTableContainer(props: OrdersTableContainerProps) {
  const { orders = emptyOrders, totalCount, onRefresh } = props

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null)
  const [convertDialogOpen, setConvertDialogOpen] = useState(false)

  const [createConsultation] = useCreateConsultationMutation()
  const [updateMeetingLinkOrder] = useUpdateMeetingLinkOrderMutation()
  const [searchOrders, { isFetching }] = useLazySearchOrdersQuery({
    refetchOnFocus: true,
    refetchOnReconnect: true,
  })

  const handleRefresh = async () => {
    try {
      await searchOrders({
        query: {},
        options: {
          limit: 10,
          skip: 0,
          order: 'DESC',
          orderBy: 'startDate',
        },
      }).unwrap()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.order.search.error')), { variant: 'error' })
    }
  }

  const ordersData = useMemo(
    () =>
      _.map(orders, (order) => ({
        id: order._id,
        consultationId: order.consultation?._id,
        meetingPlace: order.meetingPlace.placeId,
        meetingPlaceDescription: order.meetingPlace.description,
        patient: order.createdBy
          ? {
              id: order.createdBy._id,
              fullName: order.createdBy.userInfo.name + ' ' + order.createdBy.userInfo.surname,
            }
          : null,
        speciality: order.speciality,
        startDate: order.startDate,
        status: order.status as string,
        filesCount: order.files?.length || 0,
        isPrioritized: order.meetingLink?.isPrioritized || false,
         comment: order.comment,
        referenceId: order.referenceId,
        hasFormSubmission: !!order.formSubmission,
        payment: order.payment,
        requiresPayment: order.requiresPayment,
        paymentCompleted: order.paymentCompleted,
        createdAt: order.createdAt,
      })),
    [orders]
  )


  const handleOrderUpdate = async (orderId: string, status: OrderStatus, additionalData = {}) => {
    try {
      await updateMeetingLinkOrder({ id: orderId, status, ...additionalData }).unwrap()
      handleRefresh()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t(`notification.order.${status.toLowerCase()}.error`)), { variant: 'error' })
    }
  }

  const handleAcceptOrder = (orderId: string) => {
    if (!authUser) return
    handleOrderUpdate(orderId, OrderStatus.Accepted, { acceptedBy: authUser._id })
  }

  const handleCancelOrder = (orderId: string) => {
    handleOrderUpdate(orderId, OrderStatus.Canceled)
  }

  const handleRefuseOrder = (orderId: string) => {
    handleOrderUpdate(orderId, OrderStatus.Refused)
  }

  const handleStartLocalConsultation = async (orderId: string, patientId: string) => {
    try {
      const consultation = await createConsultation({
        patient: patientId,
        type: ConsultationType.Local,
        status: ConsultationStatus.Active,
        startDate: new Date().toISOString(),
      }).unwrap()

      await updateMeetingLinkOrder({
        id: orderId,
        consultation: consultation._id,
        status: OrderStatus.Consultation_Created,
      }).unwrap()

      handleRefresh()
      navigate(`/consultations/${consultation._id}/local`)
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.consultation.create.error')), { variant: 'error' })
    }
  }

  const handleConvertOrder = (orderId: string) => {
    setSelectedOrderId(orderId)
    setConvertDialogOpen(true)
  }

  const handleCloseConvertDialog = () => {
    setSelectedOrderId(null)
    setConvertDialogOpen(false)
  }

  const commonProps = {
    data: ordersData,
    loading: isFetching,
    onAcceptOrder: handleAcceptOrder,
    onCancelOrder: handleCancelOrder,
    onRefuseOrder: handleRefuseOrder,
    onStartLocalConsultation: handleStartLocalConsultation,
    onRefresh: handleRefresh,
    onConvertOrder: handleConvertOrder,
  }

  return (
    <>
      {isMobile ? (
        <OrdersCardView {...commonProps} />
      ) : (
        <Stack alignItems="end" spacing={2}>
          <Tooltip title={t('common:button.refresh.label')}>
            <IconButton onClick={onRefresh} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <OrdersTable
            {...commonProps}
            isDoctor={isStaff(authUser) && authUser?.status === 'MEDECIN'}
            manualPagination={false}
            totalCount={totalCount ?? orders.length}
          />
        </Stack>
      )}

      {selectedOrderId && (
        <ConvertOrderDialogContainer
          open={convertDialogOpen}
          onClose={handleCloseConvertDialog}
          orderId={selectedOrderId}
          meetingPlace={ordersData.find((order) => order.id === selectedOrderId)?.meetingPlace as AvailabilityMeetingPlace}
          onSuccess={onRefresh}
        />
      )}
    </>
  )
}
